/** Pure derivation behind the scope ledger: members grouped under ONE header
 *  per WBS LEAF — the hierarchy ABOVE the leaf rides as a breadcrumb label on
 *  that header, never as its own rows. Groups keep the members' incoming
 *  order (scope-members arrives in WBS-tree order), so leaves picked from
 *  completely different areas still read in WBS order. No React, no IO. */
import type { LaneMember } from "./ScopeLedger";

const NO_WBS = "(no WBS node)";

const pathOf = (lm: LaneMember): string[] =>
  lm.member.wbs_path.length > 0 ? lm.member.wbs_path : [NO_WBS];

export interface LeafListGroup {
  /** Stable collapse key — the space-joined path (same shape as the board's
   *  leaf keys). */
  key: string;
  /** The leaf's own name. */
  label: string;
  /** The ancestors above it, root first. */
  breadcrumb: string[];
  /** Members in date order (start, then task_code). */
  members: LaneMember[];
}

export function buildLeafList(members: LaneMember[]): LeafListGroup[] {
  const order: string[] = [];
  const byKey = new Map<string, LeafListGroup>();
  for (const lm of members) {
    const path = pathOf(lm);
    const key = path.join(" ");
    let g = byKey.get(key);
    if (!g) {
      g = {
        key,
        label: path[path.length - 1],
        breadcrumb: path.slice(0, -1),
        members: [],
      };
      byKey.set(key, g);
      order.push(key);
    }
    g.members.push(lm);
  }
  byKey.forEach((g) => {
    g.members.sort(
      (a, b) =>
        a.startIndex - b.startIndex ||
        a.member.task_code.localeCompare(b.member.task_code),
    );
  });
  return order.map((k) => byKey.get(k)!);
}
